/**
 * The decidable logic behind the service worker's `push` and
 * `notificationclick` handlers — extracted so it can be unit-tested.
 *
 * A service worker cannot be exercised by vitest/jsdom directly (no real
 * `PushEvent`, `NotificationEvent` or `Clients`), so every decision that does
 * NOT require an actual browser API is pulled out here as a pure function and
 * tested against it. `apps/web/public/sw.js` is the thing that actually runs
 * in the worker; it REIMPLEMENTS these functions verbatim in plain JS rather
 * than importing this file, because `public/` ships unprocessed — there is no
 * TypeScript compilation and no bundler module resolution for it, so an
 * `import './swLogic.ts'` from `public/sw.js` would not resolve in the built
 * output. Keep the two copies in sync by hand; a drift here is not a style
 * nit, it is a live vulnerability (see `resolveSafeDeepLink` below).
 *
 * Authority: ADR-0006 §3 (payload privacy), ADR-0008 §5 (update handshake),
 * `.claude/skills/notifications/SKILL.md`, `.claude/skills/pwa-shell/SKILL.md`.
 */

// ---------------------------------------------------------------------------
// push: payload validation
// ---------------------------------------------------------------------------

/**
 * The only shape a push payload may ever have — mirrors
 * `apps/bff/src/push-payload.ts`'s `PushPayload`. All five fields are
 * required, non-empty strings; there is no optional field and no fallback.
 */
export interface PushNotificationPayload {
  readonly kind: string;
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly url: string;
}

const REQUIRED_PUSH_PAYLOAD_FIELDS = ['kind', 'id', 'title', 'body', 'url'] as const;

/**
 * Validate an already-decoded value against the fixed push payload shape.
 *
 * Returns `null` for anything that is not exactly right — never a partial
 * result and never a guessed fallback. The payload privacy rule (ADR-0006 §3)
 * depends on this being strict: `title` and `body` come only from the fixed,
 * reviewed catalogue the BFF composes, so a payload missing either of them is
 * not "close enough", it is untrustworthy, and the correct behaviour is to
 * show nothing.
 */
export function parsePushPayload(value: unknown): PushNotificationPayload | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;

  const record = value as Record<string, unknown>;
  for (const field of REQUIRED_PUSH_PAYLOAD_FIELDS) {
    if (typeof record[field] !== 'string' || record[field].length === 0) return null;
  }

  return {
    kind: record['kind'] as string,
    id: record['id'] as string,
    title: record['title'] as string,
    body: record['body'] as string,
    url: record['url'] as string,
  };
}

/**
 * Decode and validate the raw text of a push message.
 *
 * Mirrors what the worker does with `event.data.text()`: garbage JSON, an
 * empty body, or `null` (no data at all) all resolve to `null` here — "show
 * nothing rather than guessing", exactly as missing/unparseable is handled.
 */
export function parsePushPayloadFromText(text: string | null | undefined): PushNotificationPayload | null {
  if (text === null || text === undefined || text.length === 0) return null;

  let decoded: unknown;
  try {
    decoded = JSON.parse(text);
  } catch {
    return null;
  }

  return parsePushPayload(decoded);
}

// ---------------------------------------------------------------------------
// notificationclick: the deep-link check
// ---------------------------------------------------------------------------

/**
 * Verbatim copy of `apps/web/src/pages/NotificacoesList.tsx#resolveSafeDeepLink`.
 *
 * The deep link is untrusted: it arrives from `app-api-service` and is stored
 * verbatim. In `NotificacoesList` it is only ever handed to react-router's
 * client-side `navigate()`, so a bad value could not actually leave the
 * origin there. `clients.openWindow()` (used by the worker's
 * `notificationclick` handler) has no such safety net — it is a real
 * cross-origin escape if this check is skipped or weakened, so the same rules
 * apply here, in the same order:
 *
 *   1. reject tab/LF/CR FIRST — the WHATWG URL parser strips them rather than
 *      rejecting them, so `/\t/evil.example` would otherwise pass every check
 *      below (starts with one slash, no backslash) and then normalise to
 *      `//evil.example`, a protocol-relative URL to another origin;
 *   2. must start with `/` (an absolute in-app path);
 *   3. must not start with `//` (protocol-relative);
 *   4. must not contain `\` (some URL parsers treat it as `/`, reintroducing #1).
 */
export function resolveSafeDeepLink(deepLink: string | null | undefined): string | null {
  if (!deepLink) return null;
  if (/[\t\n\r]/.test(deepLink)) return null;
  if (!deepLink.startsWith('/')) return null;
  if (deepLink.startsWith('//')) return null;
  if (deepLink.includes('\\')) return null;
  return deepLink;
}

// ---------------------------------------------------------------------------
// notificationclick: focus-or-open
// ---------------------------------------------------------------------------

/** The minimal shape `chooseClientAction` needs from a `WindowClient`. */
export interface ClientLike {
  readonly url: string;
}

export type ClientAction =
  /** Focus (and navigate) the existing client at this index into the input list. */
  | { readonly type: 'focus'; readonly clientIndex: number }
  /** No window is open; open a new one at the deep link. */
  | { readonly type: 'open' };

/**
 * Decide whether to focus an existing window or open a new one.
 *
 * "Focus an existing window if one is open, otherwise open the deep link" —
 * the acceptance criterion is deliberately not about matching the target URL
 * against each client's current URL: any open window of this app is a good
 * enough place to land the customer, and the worker navigates it to the deep
 * link before focusing (see `public/sw.js`). Deterministic: always the first
 * client in the list, so the same input always yields the same choice.
 */
export function chooseClientAction(existingClients: readonly ClientLike[]): ClientAction {
  return existingClients.length > 0 ? { type: 'focus', clientIndex: 0 } : { type: 'open' };
}
